/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CheckboxInput, Table, type TableColumn, Tag, Typography2 } from '@coursera/cds-core';

import { compareByNaturalSortOrder } from 'bundles/author-common/utils/common';
import StaffPermissionsLink from 'bundles/staff-management/src/StaffPermissionsLink';
import RowActions from 'bundles/staff-management/src/cds/StaffTable/CDSLikeTable/components/RowActions';
import CDSTableBulkActions from 'bundles/staff-management/src/cds/StaffTable/CDSTableBulkActions';
import { CDSTableEmptyState } from 'bundles/staff-management/src/cds/StaffTable/CDSTableEmptyState';
import type { StaffTableRow, StaffTableRowKey } from 'bundles/staff-management/src/cds/StaffTable/types';

import _t from 'i18n!nls/staff-management';

type Row = StaffTableRow & { selected?: boolean; actions?: React.ReactNode };

type RowKey = keyof Row;

const styles = {
  container: css`
    position: relative;

    td:first-of-type,
    th:first-of-type {
      min-width: 72px;
      max-width: 72px;
      width: 72px;
    }

    td:nth-of-type(2),
    th:nth-of-type(2) button {
      padding-left: 0;
    }

    figcaption {
      margin-bottom: 0 !important;
    }
  `,
  checkbox: css`
    svg {
      display: block !important;
    }
  `,
  bulkActions: css`
    margin-top: -60px;
  `,
  pendingInviteTag: css`
    margin-top: var(--cds-spacing-50);
  `,
};

type Sort = { key: StaffTableRowKey; direction: 'ascending' | 'descending' };

const sortComparator = (rowA: Row, rowB: Row, sort: Sort) => {
  const [firstRow, secondRow] = sort.direction === 'ascending' ? [rowA, rowB] : [rowB, rowA];

  const firstVal = firstRow[sort.key];
  const secondVal = secondRow[sort.key];

  return compareByNaturalSortOrder(String(firstVal), String(secondVal));
};

const getSortedRows = (rows: Row[], sort: Sort) => {
  const newRows = [...rows];
  newRows.sort((rowA, rowB) => sortComparator(rowA, rowB, sort));
  return newRows;
};

export const CDSTable = ({
  rows,
  totalRows = 0,
  onBatchDelete,
  onBatchEdit,
  ...props
}: {
  rows: StaffTableRow[];
  totalRows?: number;
  onBatchDelete: (rows: StaffTableRow[]) => void;
  onBatchEdit: (rows: StaffTableRow[]) => void;
}) => {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectedAll, setSelectedAll] = React.useState(false);
  const [selected, setSelected] = React.useState<Row[]>([]);
  const [sort, setSort] = React.useState<Sort>({
    key: 'fullName',
    direction: 'ascending',
  });
  const sortedRows = React.useMemo(() => getSortedRows(rows, sort), [rows, sort]);
  const displayedRows = React.useMemo(
    () => sortedRows.slice(page * pageSize, (page + 1) * pageSize),
    [sortedRows, page, pageSize]
  );

  const columns: Array<TableColumn<Row, RowKey>> = [
    {
      accessor: 'selected',
      label: (
        <span>
          <CheckboxInput
            css={styles.checkbox}
            checked={selectedAll}
            onChange={() => setSelectedAll(true)}
            disabled={rows.length === 0}
          />
        </span>
      ) as unknown as string,
      width: '24px',
      renderCell: (row) => (
        <CheckboxInput
          checked={selected.some((_) => _.id === row.id) || selectedAll}
          disabled={selectedAll}
          onChange={() => {
            setSelected((old) =>
              old.some((_) => _.id === row.id) ? old.filter((_) => _.id !== row.id) : old.concat(row)
            );
          }}
        />
      ),
    },
    {
      accessor: 'fullName',
      width: '320px',
      label: _t('#{count} Staff', { count: rows.length }),
      renderCell: (row) => {
        const invitePending = !row.fullName; // TODO: replace with actual invite pending property
        return (
          <div className="pii-hide fs-exclude">
            {row.fullName && <StaffPermissionsLink id={row.id}>{row.fullName}</StaffPermissionsLink>}
            <Typography2 component="div" variant="bodySecondary" color="supportText" data-e2e="row-email">
              {row.email}
            </Typography2>
            {invitePending && <Tag css={styles.pendingInviteTag}>{_t('Pending invite')}</Tag>}
          </div>
        );
      },
      sortable: true,
      sortComparator: (rowA, rowB) => sortComparator(rowA, rowB, sort),
    },
    {
      accessor: 'roles',
      label: _t('Roles'),
      renderCell: (row) => <div>{row.rolesLabel}</div>,
    },
    {
      accessor: 'actions',
      justify: 'end',
      'aria-label': _t('Staff actions'), // When label is empty, aria-label must be provided
      renderCell: (row) => <RowActions row={row} />,
    },
  ];
  const showBulkActions = Boolean(selectedAll || selected.length);
  let status: 'idle' | 'empty' = 'idle';
  if (totalRows === 0 || rows.length === 0) {
    status = 'empty';
  }
  return (
    <div css={styles.container} {...props}>
      {showBulkActions && (
        <CDSTableBulkActions
          selectedAll={selectedAll}
          selectedCount={selectedAll ? totalRows : selected.length}
          onUnselect={() => {
            setSelectedAll(false);
            setSelected([]);
          }}
          onBatchEdit={() => onBatchEdit(selectedAll ? rows : selected)}
          onBatchDelete={() => onBatchDelete(selectedAll ? rows : selected)}
        />
      )}
      <div css={showBulkActions ? styles.bulkActions : undefined}>
        <Table<Row, RowKey>
          serverMode
          status={status}
          aria-label={_t('Staff')}
          rows={displayedRows}
          columns={columns}
          totalRows={rows.length}
          sortKey={sort.key}
          sortDirection={sort.direction}
          onSort={(accessor, sortOrder) => {
            setSort({ key: accessor as StaffTableRowKey, direction: sortOrder });
          }}
          pageSize={pageSize}
          onPageSizeChange={(_, size) => {
            setPageSize(size);
            setPage(0);
          }}
          page={page + 1}
          onPageChange={(_, newPage) => {
            setPage(newPage - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          renderStatus={(statusToRender) => {
            if (statusToRender === 'empty') {
              const label = totalRows === 0 ? _t('No staff have been invited yet') : _t('No staff matched');
              return <CDSTableEmptyState label={label} />;
            }
            return null;
          }}
        />
      </div>
    </div>
  );
};
